import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  assignAutoEscalationProfile,
  assignAutoEscalationQuery,
  rebuildAutoEscalationCron,
  type AssignableTicketType,
} from '../../api/settingsEaseedeskAssignAutoEscalation'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Assigning auto escalation.
 *
 * Element cu element fata de `version-3/easeedesk_settings/escalation/
 * assigning_auto_escalation/index.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/escalation/assigning_auto_escalation/
 * index-1.js`: alegerea din casuta se salveaza pe loc, fara buton.
 *
 * Fiecare pereche tip-de-tichet + STARE poate primi un profil de escaladare
 * automata: "un tichet de tipul asta, ramas in starea asta, pleaca dupa atatea
 * minute catre grupul acela". Profilele se fac in fila de alaturi.
 * "Select.." scoate legatura.
 */
function TypeRow({
  type,
  profiles,
  onSaved,
}: {
  type: AssignableTicketType
  profiles: { ID: number; profile_name: string }[]
  onSaved(): void
}) {
  /*
   * Ce a ales omul, cat timp raspunsul e pe drum.
   *
   * `value` se lua din `page.data`, care nu se schimba decat DUPA ce serverul a
   * raspuns: casuta sarea inapoi la "Select.." in clipa alegerii si se intorcea
   * o secunda mai tarziu. Si cand serverul REFUZA (profilul altui furnizor,
   * starea din afara fluxului) ramanea pe ecran alegerea refuzata, cu un singur
   * toast care trecea. Acum se tine aici pana la raspuns, si se sterge in
   * amandoua cazurile - la reusita fiindca vin datele noi, la refuz fiindca
   * ecranul trebuie sa arate ce e CHIAR salvat.
   */
  const [pending, setPending] = useState<Record<number, number>>({})

  const save = useMutation({
    mutationFn: (v: { status: number; profile: number }) =>
      assignAutoEscalationProfile(type.ID, v.status, v.profile),
    onSuccess: (_answer, v) => {
      setPending((was) => {
        const next = { ...was }
        delete next[v.status]
        return next
      })
      toast('Profile assigned.', 'Success')
      onSaved()
    },
    onError: (error, v) => {
      setPending((was) => {
        const next = { ...was }
        delete next[v.status]
        return next
      })
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
    },
  })

  return (
    <tr>
      <td className="center">
        {/*
         * `od_console.css` da fiecarui `.badge` cerneala inchisa
         * (`--od-ink-soft`, #5b6672) si macheta scrie doar fundalul, deci
         * prescurtarea se citea inchis pe culoarea tipului. In portal era
         * alba, din `.badge` al lui Bootstrap.
         */}
        <span className="badge"
          style={{ minWidth: 23, backgroundColor: type.color_identifier, color: '#fff', textAlign: 'center' }}>
          {type.short_name}
        </span>
      </td>
      <td>{type.type_name}</td>
      <td>
        {type.workflow_id > 0 ? (
          <table width="100%">
            <tbody>
              {type.statuses.map((status) => (
                <tr key={status.ID}>
                  <td style={{ border: 'none', padding: 4 }}>{status.status_name}</td>
                  <td style={{ border: 'none', padding: 4 }}>
                    <select
                      className="form-control change-profile"
                      value={pending[status.ID] ?? status.profile_id}
                      disabled={save.isPending}
                      aria-label={`Auto escalation profile for ${type.type_name} - ${status.status_name}`}
                      onChange={(e) => {
                        const profile = Number(e.target.value)
                        setPending((was) => ({ ...was, [status.ID]: profile }))
                        save.mutate({ status: status.ID, profile })
                      }}
                    >
                      <option value={0}>Select..</option>
                      {profiles.map((profile) => (
                        <option key={profile.ID} value={profile.ID}>
                          {profile.profile_name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="alert alert-warning" style={{ margin: 0 }}>
            <i className="fas fa-info-circle" aria-hidden="true"></i> No workflow assigned on this type.
          </div>
        )}
      </td>
    </tr>
  )
}

export function AssignAutoEscalationPage() {
  const queryClient = useQueryClient()
  const page = useQuery(assignAutoEscalationQuery())

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'assigning_auto_escalation'] })
  }

  const rebuild = useMutation({
    mutationFn: () => rebuildAutoEscalationCron(),
    onSuccess: (answer) =>
      answer.cron === 'failed'
        ? toast(answer.cron_message || 'The cron server did not answer.', 'Error', 'error')
        : toast('Configuration build successfully.', 'Success'),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <LoadFailed what="Assigning auto escalation" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  return (
    <div>
      <SettingsHead
        strong="Assigning"
        rest="auto escalation"
        subtitle={
          <>
            Each type of ticket can have assigned an <strong>auto escalation profile</strong>
          </>
        }
      />

      <section className="settings">
        <div className="row">
          <div className="col-md-12">
            <div className="form-group row">
              <div className="col-md-12">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={rebuild.isPending}
                  onClick={() => rebuild.mutate()}
                >
                  <i className="fas fa-sync-alt" aria-hidden="true"></i> Refresh cron
                </button>
                <span className="help-block">Refresh auto escalation cron after every change.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="settings">
        <div className="row">
          <div className="col-md-12">
            <table className="table table-striped table-hover profile-assigning">
              <thead>
                <tr>
                  <th width={100}></th>
                  <th className="a-left">Ticket type</th>
                  <th>Assigned profile</th>
                </tr>
              </thead>
              <tbody>
                {page.data.ticket_types.map((type) => (
                  <TypeRow key={type.ID} type={type} profiles={page.data.profiles} onSaved={reload} />
                ))}
                {page.data.ticket_types.length === 0 && (
                  <tr>
                    <td colSpan={3}>
                      <div className="alert alert-info" style={{ marginBottom: 0 }}>
                        <i className="fas fa-info-circle" aria-hidden="true"></i> No active ticket types found. You must
                        activate the ticket types first.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  )
}
